import "./App.css";
import {
  Navigate,
  Route,
  BrowserRouter as Router,
  Routes,
  useLocation,
} from "react-router-dom";
import { lazy, Suspense, useContext, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Box, CircularProgress } from "@mui/material";
import { DataContext, useData } from "./contexts/DataProvider";
import { useSessionHeartbeat } from "./hooks/useSessionHeartbeat";
import { useEnvironmentTitle } from "./hooks/useEnvironmentTitle";
import ScheduleMeetContainer from "./schedulemeet/pages/ScheduleMeetContainer";
import ClientHistory from "./meeting/Summary";
import Sucess from "./usageandbilling/Sucess";
import Cancel from "./usageandbilling/Cancel";
import ClientManagement from "./meeting/Client";
import UploadDocumentTemplate from "./admin/UploadDocumentTemplate";
import UserPage from "./users/UserPage";
import LoginPage1 from "./pages/NewLoginPage";
import VerifyAccessPage from "./pages/VerifyAccessPage";
import GlobalAdminPage from "./global-admin/GlobalAdminPage";
import Organisations from "./users/OrganisationsPage";
import FileListUI from "./components/FileShare";
import FileSharePage from "./pages/FileSharePage";
import FileViewerPage from "./pages/FileViewerPage";
import SearchableTable from "./meeting/ClientSearch";
import HelpCenter from "./components/HelpDesk/HelpCenter";
import Homepage from "./components/Homepage";
import ClientsPage from "./pages/ClientsPage";
import ContactsPage from "./pages/ContactsPage";
import LoadingIndicator from "./components/common/LoadingIndicator";
import { FloatingChat } from "./components/FloatingChat/FloatingChat";
import { DynamicThemeProvider } from "./components/DynamicThemeProvider";
import StripeDemo from "./stripe/PaymentWrapper";
import Manual from "./components/manual/Manual";
import TermsOfServiceModal from "./components/TermsOfServiceModal";
import FreeTierLimitModal from "./components/FreeTierLimitModal";
import { acceptTerms, acceptTOS } from "./services/auth.service";
import { hasExperimentalFeature } from "./constants/experimentalFeatures";
import { useInactivityTimeout } from "./hooks/useInactivityTimeout";
import SessionTimeoutModal from "./components/SessionTimeoutModal";
import { ChunkErrorBoundary } from "./components/ChunkErrorBoundary";
import ImpersonationOverlay from "./components/ImpersonationOverlay/ImpersonationOverlay";

// Retries the dynamic import once on failure (handles transient network blips
// and stale chunk hashes from new deployments).
const lazyWithRetry = (importFn: () => Promise<{ default: any }>) =>
  lazy(() => importFn().catch(() => importFn()));

const LoginPage = lazyWithRetry(() => import("./pages/LoginPage"));
const SignupPage = lazyWithRetry(() => import("./pages/SignupPage"));
const AlertProvider = lazyWithRetry(() => import("./components/AlertProvider"));
const MeetingContainer = lazyWithRetry(
  () => import("./meeting/MeetingContainer"),
);
const MeetingDetails = lazyWithRetry(
  () => import("./meeting/MeetingDetails/MeetingDetails"),
);
const ResetSidebar = lazyWithRetry(() => import("./components/ResetSidebar"));
const DocumentsPage = lazyWithRetry(() => import("./pages/documents/DocumentsPage"));
const FinancialsPage = lazyWithRetry(() => import("./pages/billing/FinancialsPage"));
const DocumentEditorPage = lazyWithRetry(
  () => import("./pages/documents/DocumentEditorPage"),
);
const WikiListPage = lazyWithRetry(() => import("./pages/wiki/WikiListPage"));
const WikiEditorPage = lazyWithRetry(() => import("./pages/wiki/WikiEditorPage"));
const ForgotPassword = lazyWithRetry(() => import("./pages/ForgotPassword"));
const ResetPassword = lazyWithRetry(() => import("./pages/ResetPassword"));
const CheckYourEmailPage = lazyWithRetry(
  () => import("./pages/CheckYourEmailPage"),
);
const VerifyEmailPage = lazyWithRetry(() => import("./pages/VerifyEmailPage"));
const AcceptInvitePage = lazyWithRetry(
  () => import("./pages/AcceptInvitePage"),
);
const PolicyDocPage = lazyWithRetry(() => import("./pages/PolicyDocPage"));
const EmailPage = lazyWithRetry(() => import("./email/EmailPage"));
const PrivateFilesPage = lazyWithRetry(() => import("./pages/PrivateFilesPage"));

/** Legacy bookmarks and emails still use `/organisations`; keep query string (e.g. ?tab=payments). */
function LegacyOrganisationsRedirect() {
  const { search } = useLocation();
  return <Navigate to={{ pathname: "/admin", search }} replace />;
}

/**
 * Renders the fallback title/robots tag. Must stay mounted for the app's entire
 * lifetime (rendered once per top-level branch below) so that when a page-level
 * <SeoHead> unmounts — e.g. LoginPage after a successful sign-in — Helmet's title
 * stack has something to fall back to instead of leaving the previous page's
 * title stuck in the browser tab.
 */
function DefaultSeo() {
  const envTitle = useEnvironmentTitle();
  return (
    <Helmet>
      <title>{envTitle}</title>
      <meta name="robots" content="noindex, nofollow" />
    </Helmet>
  );
}

// Component for authenticated users that includes session heartbeat
function AuthenticatedApp() {
  const { login, setLogin } = useContext(DataContext);
  const isGlobalAdmin = login?.data?.role === "global admin";
  // Only treat TOS as accepted when the flag is explicitly true or when
  // the user is a global admin. (Undefined should mean not yet accepted.)
  const [tosAccepted, setTosAccepted] = useState(
    isGlobalAdmin || !!(login?.data?.hasAcceptedToS === true),
  );
  const [tosLoading, setTosLoading] = useState(false);

  // Initialize session heartbeat for authenticated users
  const { logout } = useData();
  const { showWarning, resetTimers } = useInactivityTimeout(logout);
  useSessionHeartbeat();

  const handleTosAccepted = () => {
    setTosAccepted(true);
    setLogin((prev: any) => ({
      ...prev,
      data: { ...prev.data, hasAcceptedToS: true },
    }));
  };

  const handleTosAcceptWithHash = async ({
    tosHash,
    ppHash,
  }: {
    tosHash: string;
    ppHash: string;
  }) => {
    setTosLoading(true);
    try {
      // 1. Record both document acceptances in parallel (non-fatal if either fails)
      await Promise.all([
        acceptTerms({
          version: "1.0",
          type: "TERMS_OF_SERVICE",
          documentHash: tosHash,
          method: "checkbox",
        }).catch((e: any) =>
          console.error("[ToS] Failed to record ToS acceptance:", e),
        ),
        acceptTerms({
          version: "1.0",
          type: "PRIVACY_POLICY",
          documentHash: ppHash,
          method: "checkbox",
        }).catch((e: any) =>
          console.error("[ToS] Failed to record Privacy Policy acceptance:", e),
        ),
      ]);

      // 2. Get fresh JWT reflecting acceptance
      const res = (await acceptTOS()) as any;
      if (res?.token) {
        localStorage.setItem("token", res.token);
      }
      handleTosAccepted();
    } catch (e) {
      console.error("[ToS] Failed to accept documents:", e);
    } finally {
      setTosLoading(false);
    }
  };

  return (
    <>
      <DefaultSeo />
      <SessionTimeoutModal
        open={showWarning}
        onStayLoggedIn={resetTimers}
        onLogout={logout}
      />
      <AlertProvider />
      <ImpersonationOverlay />
      <Router>
        <FreeTierLimitModal />
        <ChunkErrorBoundary>
          <Suspense
            fallback={
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                height="100vh"
              >
                <CircularProgress size={80} />
              </Box>
            }
          >
            <LoadingIndicator>
              <DynamicThemeProvider>
                <ResetSidebar />
                <Routes>
                  <Route path="/login" element={<LoginPage />} />
                  <Route
                    path="/check-your-email"
                    element={<Navigate to="/home" replace />}
                  />
                  <Route
                    path="/verify-email"
                    element={<Navigate to="/home" replace />}
                  />
                  <Route path="/accept-invite" element={<AcceptInvitePage />} />

                  <Route path="/" element={<Navigate to="/home" />} />
                  {/* Legacy redirects — keep old bookmarks working */}
                  <Route
                    path="/billings"
                    element={
                      <Navigate to="/global-admin?tab=analytics" replace />
                    }
                  />
                  <Route
                    path="/organisations"
                    element={<LegacyOrganisationsRedirect />}
                  />
                  <Route
                    path="/diagnostics"
                    element={
                      <Navigate to="/global-admin?tab=diagnostics" replace />
                    }
                  />
                  <Route
                    path="/recycle-bin"
                    element={
                      <Navigate to="/global-admin?tab=recycle-bin" replace />
                    }
                  />

                  <Route
                    path="/admin"
                    element={
                      ["global admin", "orgadmin"].includes(login?.data?.role) ? (
                        <Organisations isAdmin={true} />
                      ) : (
                        <Navigate to="/home" replace />
                      )
                    }
                  />
                  <Route
                    path="/test-suites"
                    element={
                      login?.data?.role === "global admin" ? (
                        <Navigate to="/global-admin?tab=test-suites" replace />
                      ) : (
                        <Navigate to="/home" replace />
                      )
                    }
                  />
                  <Route
                    path="/global-admin"
                    element={
                      login?.data?.role === "global admin" ? (
                        <GlobalAdminPage />
                      ) : (
                        <Navigate to="/home" replace />
                      )
                    }
                  />
                  <Route path="/login1" element={<LoginPage1 />} />
                  <Route path="/signup" element={<SignupPage />} />
                  <Route path="/verify-access" element={<VerifyAccessPage />} />
                  <Route path="/privacy" element={<PolicyDocPage />} />
                  <Route path="/success" element={<Sucess />} />
                  <Route path="/cancel" element={<Cancel />} />
                  <Route path="/stripe" element={<StripeDemo />} />
                  <Route path="/profile" element={<UserPage />} />
                  <Route path="/helpcenter" element={<HelpCenter />} />
                  <Route path="/notes"     element={<WikiListPage />} />
                  <Route path="/notes/:id" element={<WikiEditorPage />} />
                  <Route
                    path="/*"
                    element={
                      <Routes>
                        <Route
                          path="/scheduler/*"
                          element={<ScheduleMeetContainer />}
                        />
                        <Route path="/FileListUI" element={<FileListUI />} />
                        <Route path="/files" element={<FileSharePage />} />
                        {/* Read-only new-tab preview so "Open in Browser" renders
                            a file instead of downloading it. */}
                        <Route
                          path="/files/view/:itemId"
                          element={<FileViewerPage />}
                        />
                        <Route path="/private-files" element={<PrivateFilesPage />} />
                        <Route path="/Manual" element={<Manual />} />
                        <Route
                          path="/client-search"
                          element={<SearchableTable />}
                        />
                        <Route
                          path="/meetings/:meetingId"
                          element={<MeetingDetails />}
                        />
                        <Route path="/client" element={<ClientManagement />} />
                        <Route
                          path="/upload"
                          element={<UploadDocumentTemplate />}
                        />
                        <Route path="/home/*" element={<Homepage />} />
                        <Route path="/clients" element={<ClientsPage />} />
                        <Route path="/contacts" element={<ContactsPage />} />
                        {hasExperimentalFeature(
                          login?.data,
                          "revenue_billing",
                        ) && (
                          <>
                            <Route path="/revenue" element={<FinancialsPage />} />
                            <Route path="/billing" element={<FinancialsPage />} />
                          </>
                        )}
                        {hasExperimentalFeature(
                          login?.data,
                          "documents",
                        ) && (
                          <>
                            <Route
                              path="/documents"
                              element={<DocumentsPage />}
                            />
                            <Route
                              path="/documents/:id"
                              element={<DocumentEditorPage />}
                            />
                          </>
                        )}
                        <Route path="/email" element={<EmailPage />} />
                        <Route path="/email/*" element={<EmailPage />} />
                        <Route
                          path="/client/:clientId/*"
                          element={<MeetingContainer />}
                        />
                        <Route
                          path="/client-history1"
                          element={<ClientHistory />}
                        />
                        <Route path="/*" element={<Navigate to="/home" />} />
                      </Routes>
                    }
                  />
                </Routes>

                {hasExperimentalFeature(login?.data, "prospo_agent") && (
                  <FloatingChat />
                )}
              </DynamicThemeProvider>
            </LoadingIndicator>
          </Suspense>
        </ChunkErrorBoundary>
      </Router>
      <TermsOfServiceModal
        open={!tosAccepted}
        onClose={() => {}}
        onAccept={handleTosAcceptWithHash}
        loading={tosLoading}
        disableDismiss={true}
      />
    </>
  );
}

function App() {
  const { login } = useContext(DataContext);

  if (login.isLoggedIn === undefined) {
    return (
      <>
        <DefaultSeo />
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          height="100vh"
        >
          <CircularProgress size={80} />
        </Box>
      </>
    );
  }
  if (login.isLoggedIn === false) {
    return (
      <Router>
        <DefaultSeo />
        <AlertProvider />
        <ChunkErrorBoundary>
          <Suspense
            fallback={
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                height="100vh"
              >
                <CircularProgress />
              </Box>
            }
          >
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/signup" element={<SignupPage />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/verify-access" element={<VerifyAccessPage />} />
              <Route
                path="/check-your-email"
                element={<CheckYourEmailPage />}
              />
              <Route path="/verify-email" element={<VerifyEmailPage />} />
              <Route path="/accept-invite" element={<AcceptInvitePage />} />
              <Route path="/privacy" element={<PolicyDocPage />} />
              <Route path="/*" element={<Navigate to="/login" />} />
            </Routes>
          </Suspense>
        </ChunkErrorBoundary>
      </Router>
    );
  }

  // Return authenticated app with session heartbeat
  return <AuthenticatedApp />;
}

export default App;
